import { makeObservable, observable } from 'mobx';
import type { Point, Element, ToolType, ElementStyle } from '@/types';
import type { RootStore } from '@/stores/RootStore';

export interface ToolContext {
  rootStore: RootStore;
  startPoint: Point;
  currentPoint: Point;
  isDragging: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  elements?: Element[];
  persistentStyle: ElementStyle;
}

export abstract class Tool {
  public readonly type: ToolType;
  public readonly name: string;

  constructor(type: ToolType, name: string) {
    this.type = type;
    this.name = name;

    makeObservable(this, {
      type: observable,
      name: observable,
    });
  }

  /**
   * Called when the tool starts being used (mouse down)
   */
  abstract onStart(context: ToolContext): Element | null;

  /**
   * Called while the tool is being used (mouse move)
   */
  abstract onMove(context: ToolContext): Element | null | undefined;

  /**
   * Called when the tool finishes being used (mouse up)
   */
  abstract onEnd(context: ToolContext): Element | null | void;

  /**
   * Called when the tool usage is cancelled (escape key)
   */
  onCancel(_context: ToolContext): void {
    // Default implementation - override if needed
  }

  /**
   * Get the cursor style for this tool
   */
  getCursor(_context?: ToolContext): string {
    return 'default';
  }

  /**
   * Validate if the tool can create an element with given context
   */
  protected validateElement(context: ToolContext): boolean {
    const { startPoint, currentPoint } = context;
    const minDistance = 5; // Minimum distance to create an element

    const dx = Math.abs(currentPoint.x - startPoint.x);
    const dy = Math.abs(currentPoint.y - startPoint.y);

    return dx >= minDistance || dy >= minDistance;
  }

  /**
   * Calculate bounds from start and current points
   */
  public getBounds(context: { startPoint: Point; currentPoint: Point }) {
    const { startPoint, currentPoint } = context;

    return {
      x: Math.min(startPoint.x, currentPoint.x),
      y: Math.min(startPoint.y, currentPoint.y),
      width: Math.abs(currentPoint.x - startPoint.x),
      height: Math.abs(currentPoint.y - startPoint.y),
    };
  }

  /**
   * Generate a temporary ID for preview elements
   */
  protected generatePreviewId(): string {
    return `preview-${this.type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  onDoubleClick?(context: ToolContext): void;
}
